import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useState } from 'react';

const CVSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0,
    rootMargin: '0px',
  });
  const [downloadError, setDownloadError] = useState(null);

  const handleDownload = () => {
    try {
      // Define the CV filename
      const fileName = 'Gerald_Suleh_-_Senior_Engineer-SOC.pdf';
      
      // Get CV file directly from the public folder with proper path resolution
      const cvFilePath = `${window.location.origin}${process.env.PUBLIC_URL}/${fileName}`;
      
      console.log('Attempting to download CV from:', cvFilePath);
      
      // Check if file exists before downloading (using a HEAD request)
      fetch(cvFilePath, { method: 'HEAD' })
        .then(response => {
          if (response.ok) {
            // File exists, proceed with download
            const link = document.createElement('a');
            link.href = cvFilePath;
            link.download = fileName;
            
            // Add to document, click and remove
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Clear any previous errors
            setDownloadError(null);
          } else {
            // File not found
            throw new Error(`CV file not found (${response.status}: ${response.statusText}). Please contact me directly for a copy.`);
          }
        })
        .catch(error => {
          console.error('Error checking/downloading CV:', error);
          setDownloadError('The CV file could not be downloaded. Please email me directly for a copy.');
        });
    } catch (error) {
      console.error('Error in download process:', error);
      setDownloadError('Failed to download CV. Please try again later or contact me directly.');
    }
  };

  const experiences = [
    {
      title: 'Senior Engineer - Security Operations Center',
      company: 'USIU-Africa',
      location: 'Nairobi',
      period: 'Nov 2025 - Present',
      description: 'Leading SOC establishment and operations at a major international university, combining threat monitoring with AI-driven automation and resilience engineering.',
      responsibilities: [
        'Established and operationalized the Security Operations Centre, defining core workflows, incident handling procedures, and escalation paths to enable structured and timely security response',
        'Led Security Operations activities including threat monitoring, alert triage, and incident response, improving visibility into security events across enterprise systems',
        'Researched, designed, and supported the implementation of Data Loss Prevention (DLP) controls to reduce institutional data exposure risks and improve regulatory compliance',
        'Researched and designed foundational agentic SOC models to augment analyst workflows — automating alert triage, investigation enrichment, and response decision support to improve efficiency and handle emerging AI-driven threats',
        'Developed SOC playbooks, documentation, and reporting frameworks to support consistency, audit readiness, and operational continuity'
      ]
    },
    {
      title: 'Technical Director',
      company: 'SalamaSec Ltd',
      location: 'Nairobi',
      period: 'Jan 2025 - Present',
      description: 'Premier cybersecurity firm offering VAPT and cybersecurity awareness training to businesses across various industries.',
      responsibilities: [
        'Lead delivery of technical services including vulnerability assessments, penetration testing, and infrastructure hardening for small businesses and enterprise clients',
        'Design and implement cybersecurity awareness programs to equip staff with practical knowledge on mitigating phishing, social engineering, and insider threats',
        'Oversee client engagements, reporting, and risk-based remediation strategies while ensuring alignment with best practices and compliance standards',
        'Mentor junior professionals on core cybersecurity concepts including threat modeling, secure system design, ethical hacking, and responsible disclosure'
      ]
    },
    {
      title: 'Secretary',
      company: 'Na Malkia',
      location: 'Nairobi',
      period: 'Jan 2025 - Present',
      description: 'Community organization focused on social impact initiatives and environmental awareness.',
      responsibilities: [
        'Oversee planning, logistics, and implementation of programs including the January food drive and April Uji feeding initiative for street families',
        'Support documentation, coordination with volunteers, and communication for climate and community work in Pangani',
        'Co-led design and grant proposal that secured KES 50,000 to scale up waste segregation and environmental awareness activities'
      ]
    },
    {
      title: 'Senior Backend Engineer',
      company: 'Vitraya',
      location: 'Nairobi',
      period: 'Jul 2025 - Nov 2025',
      description: 'AI-powered health insurance claims processing platform integrating document ingestion, validation, and automated decision workflows.',
      responsibilities: [
        'Built and scaled AI-driven automation components powering health insurance claim adjudication, reducing manual processing effort and enabling faster, audit-ready claim decisions across insurer and provider workflows',
        'Developed and productionized an AI-powered OCR pipeline for structured data extraction from medical and billing documents, improving data accuracy and enabling downstream automation',
        'Led development of intelligent verification systems, including an eTIMS invoice validation module, reducing fraud exposure and improving financial accuracy within claims pipelines',
        'Designed backend architectures supporting end-to-end automated claims processing, integrating document ingestion, validation, policy checks, and decision workflows into a unified system',
        'Optimized backend services and collaborated cross-functionally with product and operations teams to improve system performance and support high-volume processing environments'
      ]
    },
    {
      title: 'Research Scholar & Security Operations Centre Analyst',
      company: '@iLabAfrica - TAISOC',
      location: 'Nairobi',
      period: 'Jan 2022 - Jun 2025',
      description: 'Research and innovation center focused on information security and technology.',
      responsibilities: [
        'Conducted comprehensive Web and Mobile Application Penetration Testing to identify vulnerabilities and strengthen security postures',
        'Monitored, responded to, and analyzed Security Incidents within the SOC to mitigate risks and ensure operational resilience',
        'Drafted detailed Weekly Reports on Security Testing Results to inform stakeholders and facilitate timely remediation of identified issues',
        'Organized and delivered tailored Security Training and Workshops to enhance cybersecurity awareness and promote best practices among teams',
        'Contributed to Cybersecurity Research and Publications on child online protection, DNSSEC, routing security, and blockchain technology',
        'Developed and managed Grant Applications for Research Initiatives to secure funding and support innovative cybersecurity projects'
      ]
    },
    {
      title: 'Backend Software Engineer',
      company: 'InvestaFarm',
      location: 'Nairobi',
      period: 'Jan 2024 - Feb 2025',
      description: 'Decentralized agricultural investment platform built on the Internet Computer Protocol (ICP).',
      responsibilities: [
        'Developed backend smart contracts in Rust on the Internet Computer Protocol (ICP) for InvestaFarm\'s decentralized application, ensuring secure, efficient, and scalable business logic implementation',
        'Developed clean, efficient, and scalable server-side code to integrate AI models and handle data processing',
        'Designed and maintained contract architecture to support financial transactions, data integrity, and stakeholder interactions in a distributed environment',
        'Conducted unit testing and debugging efforts to validate core functionality and enhance system reliability before deployment',
        'Collaborated closely with front-end and product teams to integrate contracts seamlessly into the user-facing platform'
      ]
    },
    {
      title: 'Blockchain Developer',
      company: 'Africa Blockchain Centre',
      location: 'Nairobi',
      period: 'Sep 2022 - May 2024',
      description: 'Innovation hub focused on blockchain technology research and application.',
      responsibilities: [
        'Design and developed a Self-Sovereign Identity KYC solution for the Power Learn Project LMS on the Cardano network',
        'Developed a backend API to interact with the blockchain solution and parse requests from clients',
        'Researched and evaluated applications for KYC tools in different sectors including healthcare and finance',
        'Conducted stress testing and unit testing of the KYC solution',
        'Connected with key stakeholders, technical partners, and developer communities to research and develop blockchain innovations'
      ]
    },
    {
      title: 'Cybersecurity Smart Academy Research Fellow',
      company: 'Strathmore University',
      location: 'Nairobi',
      period: 'Feb 2021 - Jan 2022',
      description: 'Academic research position focused on cybersecurity, child online protection, and emerging technologies.',
      responsibilities: [
        'Research on the factors causing and promoting Online Child Sexual Exploitation and Abuse',
        'Developed content on Child Online Protection for use in training and awareness campaigns',
        'Applied for grant funding on projects focusing on Child Online Protection',
        'Cybersecurity research on the impact of emerging technologies such as blockchain and AI on malware threat detection, data protection and traffic analysis',
        'Publication of papers and reports on research work conducted at the lab'
      ]
    }
  ];

  // Publications and research
  const publications = [
    {
      title: "Data Protection Mechanisms in Mobile Money Transactions",
      year: "2023",
      publisher: "Journal of Cybersecurity Research",
      description: "Research on enhanced security protocols for mobile money transactions, focusing on encryption and authentication methods."
    },
    {
      title: "Generative AI Models for Harmful Content Detection",
      year: "2024",
      publisher: "International Conference on AI Security",
      description: "Implementation of generative AI models for detecting harmful content across digital platforms with improved accuracy rates."
    },
    {
      title: "Blockchain Solutions for Healthcare Data Interoperability",
      year: "2024",
      publisher: "Healthcare Technology Journal",
      description: "Research on blockchain frameworks for secure patient data sharing across healthcare institutions."
    }
  ];

  // Certifications
  const certifications = [
    {
      title: "Computer Hacking Forensic Investigator (C|HFI)",
      organization: "EC Council",
      year: "2024"
    },
    {
      title: "Certified Ethical Hacking (C|EH)",
      organization: "EC Council",
      year: "2024"
    },
    {
      title: "Cybersecurity Foundation",
      organization: "Palo Alto",
      year: "2022"
    },
    {
      title: "Fundamentals of Scalable Data Science",
      organization: "Coursera",
      year: "2020"
    },
    {
      title: "Data Science and Machine Learning Bootcamp with R",
      organization: "Udemy",
      year: "2020"
    }
  ];

  return (
    <section ref={ref} className="py-20">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12 text-center"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 dark:text-white mb-4">
            Curriculum Vitae
          </h2>
          <p className="text-secondary-600 dark:text-secondary-200 max-w-2xl mx-auto">
            An overview of my professional experience, technical expertise, and academic achievements.
          </p>
          <div className="w-20 h-1 bg-primary-600 mx-auto mt-4"></div>
        </motion.div>

        <div className="mb-12 flex flex-col items-center">
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            onClick={handleDownload}
            className="flex items-center bg-primary-600 hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-600 text-white font-medium py-3 px-6 rounded-md transition-colors duration-300 shadow-lg dark:shadow-black/40"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Download Full CV (Gerald_Suleh_-_Senior_Engineer-SOC.pdf)
          </motion.button>
          
          {downloadError && (
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-red-600 dark:text-red-400 mt-2 text-sm"
            >
              {downloadError}
            </motion.p>
          )}
        </div>

        {/* Professional Experience Timeline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="max-w-4xl mx-auto mb-16"
        >
          <h3 className="text-2xl font-bold text-secondary-800 dark:text-white mb-8 flex items-center">
            <svg className="w-6 h-6 mr-2 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Professional Experience
          </h3>

          <div className="space-y-10">
            {experiences.map((exp, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                className="relative pl-8 pb-8 border-l-2 border-primary-600 dark:border-primary-400 last:border-l-0 last:pb-0"
              >
                <div className="absolute left-[-9px] top-0 w-4 h-4 bg-primary-600 dark:bg-primary-500 rounded-full"></div>
                <div className="bg-white dark:bg-secondary-900 p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 dark:shadow-black/20">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-3">
                    <h4 className="text-xl font-bold text-secondary-800 dark:text-white">
                      {exp.title}
                    </h4>
                    <span className="inline-block bg-primary-100 dark:bg-primary-900/40 text-primary-800 dark:text-primary-400 text-sm font-medium px-3 py-1 rounded-full mt-2 sm:mt-0">
                      {exp.period}
                    </span>
                  </div>
                  <div className="flex items-center mb-3">
                    <p className="text-lg font-medium text-primary-600 dark:text-primary-400">
                      {exp.company}
                    </p>
                    <span className="mx-2 text-secondary-400">•</span>
                    <p className="text-secondary-600 dark:text-secondary-400">
                      {exp.location}
                    </p>
                  </div>
                  <p className="text-secondary-600 dark:text-secondary-200 mb-4 italic">
                    {exp.description}
                  </p>
                  <ul className="space-y-2 text-secondary-600 dark:text-secondary-200">
                    {exp.responsibilities.map((item, i) => (
                      <li key={i} className="flex items-start">
                        <svg className="w-5 h-5 text-primary-600 dark:text-primary-400 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Education and Certifications in two columns */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-16">
          {/* Education */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <h3 className="text-2xl font-bold text-secondary-800 dark:text-white mb-6 flex items-center">
              <svg className="w-6 h-6 mr-2 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path d="M12 14l9-5-9-5-9 5 9 5z" />
                <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
              </svg>
              Education
            </h3>
            
            <div className="space-y-4">
              <div className="bg-white dark:bg-secondary-900 p-6 rounded-lg shadow-md dark:shadow-black/20">
                <h4 className="text-lg font-bold text-secondary-800 dark:text-white mb-2">
                  Master of Science in Information Systems Security
                </h4>
                <p className="text-primary-600 dark:text-primary-400 mb-1">
                  Strathmore University
                </p>
                <p className="text-secondary-600 dark:text-secondary-200 text-sm">
                  September 2022 - November 2025
                </p>
              </div>
              
              <div className="bg-white dark:bg-secondary-900 p-6 rounded-lg shadow-md dark:shadow-black/20">
                <h4 className="text-lg font-bold text-secondary-800 dark:text-white mb-2">
                  Bachelor of Business Information Technology
                </h4>
                <p className="text-primary-600 dark:text-primary-400 mb-1">
                  Strathmore University
                </p>
                <p className="text-secondary-600 dark:text-secondary-200 text-sm">
                  April 2017 - September 2021
                </p>
                <p className="text-secondary-500 dark:text-secondary-400 text-sm mt-1 italic">
                  Second Class Upper
                </p>
              </div>
            </div>
          </motion.div>
          
          {/* Certifications */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <h3 className="text-2xl font-bold text-secondary-800 dark:text-white mb-6 flex items-center">
              <svg className="w-6 h-6 mr-2 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
              Certifications
            </h3>
            
            <div className="space-y-4">
              {certifications.map((cert, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.2 + index * 0.1 }}
                  className="bg-white dark:bg-secondary-900 p-4 rounded-lg shadow-md dark:shadow-black/20 flex items-center"
                >
                  <div className="bg-primary-100 dark:bg-primary-900/40 p-3 rounded-full mr-4">
                    <svg className="w-6 h-6 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-medium text-secondary-800 dark:text-white">
                      {cert.title}
                    </h4>
                    <p className="text-sm text-primary-600 dark:text-primary-400">
                      {cert.organization} • {cert.year}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Publications */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="max-w-4xl mx-auto"
        >
          <h3 className="text-2xl font-bold text-secondary-800 dark:text-white mb-6 flex items-center">
            <svg className="w-6 h-6 mr-2 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            Research Publications
          </h3>
          
          <div className="space-y-6">
            {publications.map((pub, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                className="bg-white dark:bg-secondary-900 p-6 rounded-lg shadow-md dark:shadow-black/20 hover:shadow-lg dark:hover:shadow-black/30 transition-shadow duration-300"
              >
                <div className="flex justify-between items-start">
                  <h4 className="text-lg font-bold text-secondary-800 dark:text-white">
                    {pub.title}
                  </h4>
                  <span className="bg-primary-100 dark:bg-primary-900/40 text-primary-800 dark:text-primary-400 text-sm font-medium px-3 py-1 rounded-full">
                    {pub.year}
                  </span>
                </div>
                <p className="text-primary-600 dark:text-primary-400 mt-2">
                  {pub.publisher}
                </p>
                <p className="text-secondary-600 dark:text-secondary-200 mt-3">
                  {pub.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Additional Skills */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="max-w-4xl mx-auto mt-16"
        >
          <h3 className="text-2xl font-bold text-secondary-800 dark:text-white mb-6 flex items-center">
            <svg className="w-6 h-6 mr-2 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
            </svg>
            Technical Skills
          </h3>
          
          <div className="bg-white dark:bg-secondary-900 p-6 rounded-lg shadow-md dark:shadow-black/20">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[
                "Penetration Testing", "Vulnerability Assessment", "Security Operations (SOC)",
                "Digital Forensics", "AI Enablement & Automation", "Agentic SOC Design",
                "Threat Modeling", "DLP Controls", "Security Consulting & Training",
                "Python", "Rust", "Go", "Node.js", "Express JS", "C#", "Kotlin", "Java", "PHP (Laravel)",
                "React JS", "Mobile App Development", "Flutter",
                "SQL", "PostgreSQL", "MongoDB", "Supabase", "Firebase",
                "API Development", "Git", "Bitbucket", "Quality Assurance", "Research & Publication"
              ].map((skill, index) => (
                <div 
                  key={index} 
                  className="bg-secondary-50 dark:bg-secondary-800 px-3 py-2 rounded-md text-secondary-800 dark:text-secondary-100 text-sm font-medium flex items-center"
                >
                  <svg className="w-4 h-4 mr-2 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  {skill}
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CVSection;

